import { useEffect, useMemo, useState } from "react";
import { useNavigate, type NavigateFunction } from "react-router-dom";
import { SchoolLayout } from "./SchoolLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import { Badge } from "@/project/karuna/components/ui/badge"; // @ts-ignore - badge is not a valid module
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/project/karuna/components/ui/tabs"; // @ts-ignore - tabs is not a valid module
import { Calendar, FileText, Eye, Award, TrendingUp, X } from "lucide-react";
import { useToast } from "@/project/karuna/hooks/use-toast"; // @ts-ignore - use-toast is not a valid module
import { useKarunaLink } from "@/project/karuna/utils/navigationRoutes";
import { DynamicModuleService } from "@/services/APIs";
import { getUserInfo } from "@/lib/storeManager";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/project/karuna/contexts/AuthContext"; // @ts-ignore - AuthContext is not a valid module
import { supabase } from "@/project/karuna/integrations/supabase/client"; // @ts-ignore - supabase is not a valid module
import {
  KarunaPagination,
  ListCardViewToggle,
  type KarunaViewMode,
} from "@/project/karuna/components/shared/KarunaListControls";
import { useKarunaPagedList } from "@/project/karuna/hooks/useKarunaPagedList";
import {
  mapWithConcurrency,
  DEFAULT_API_FETCH_CONCURRENCY,
} from "@/project/karuna/utils/mapWithConcurrency";

const SUBMISSION_SCHEMA_NAME = "Activity Submission";
const ACTIVITY_SCHEMA_NAME = "Activity Management";

interface Submission {
  id: string;
  event_id: string;
  status: string;
  score: number | null;
  submitted_at: string;
  created_at?: string;
  admin_comments: string | null;
  activity: {
    title: string;
  };
}

type SubmissionStatusTab = "all" | "approved" | "pending" | "rejected";

function SchoolSubmissionTabPanel({
  tab,
  submissions,
  navigate,
  getLink,
}: {
  tab: SubmissionStatusTab;
  submissions: Submission[];
  navigate: NavigateFunction;
  getLink: (path: string) => string;
}) {
  const [viewMode, setViewMode] = useState<KarunaViewMode>("card");
  const filtered = useMemo(
    () => submissions.filter(s => tab === "all" || s.status === tab),
    [submissions, tab]
  );

  const {
    page,
    setPage,
    totalPages,
    paginatedItems,
    totalItems,
    pageSize,
    setPageSize,
  } = useKarunaPagedList(filtered, tab);

  if (filtered.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        No {tab === "all" ? "" : tab} submissions found
      </div>
    );
  }

  const statusBadge = (submission: Submission) => (
    <Badge
      variant={
        submission.status === "approved"
          ? "default"
          : submission.status === "rejected"
            ? "destructive"
            : "secondary"
      }
    >
      {submission.status}
    </Badge>
  );

  const renderGridCard = (submission: Submission) => (
    <Card
      key={submission.id}
      className="flex w-full flex-col border-border/50 shadow-medium transition-shadow hover:shadow-md"
    >
      <CardContent className="flex flex-col gap-2.5 p-4 sm:p-5">
        <div className="space-y-2">
          <p className="line-clamp-2 text-sm font-semibold leading-snug">
            {submission.activity.title}
          </p>
          <div className="w-fit">{statusBadge(submission)}</div>
          <div className="space-y-1 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 shrink-0" />
              <span>
                {submission.submitted_at
                  ? new Date(submission.submitted_at).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      }
                    )
                  : "N/A"}
              </span>
            </div>
            {submission.score !== null &&
              submission.score !== undefined &&
              submission.score !== 0 && (
                <div className="flex items-center gap-1.5 text-primary">
                  <Award className="h-3.5 w-3.5 shrink-0" />
                  Score: {submission.score}
                </div>
              )}
          </div>
          {submission.admin_comments ? (
            <div className="rounded-md border border-border/60 bg-muted/40 p-2 text-xs">
              <p className="mb-0.5 font-medium text-muted-foreground">
                Admin note
              </p>
              <p className="line-clamp-2">{submission.admin_comments}</p>
            </div>
          ) : null}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            navigate(getLink(`/school/submissions/${submission.id}`))
          }
          className="mt-1 w-full gap-2"
        >
          <Eye className="h-4 w-4" />
          View
        </Button>
      </CardContent>
    </Card>
  );

  const renderListRow = (submission: Submission) => (
    <div
      key={submission.id}
      className="flex flex-col gap-2 border-b border-border py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="min-w-0 flex-1">
        <div className="mb-2 flex flex-wrap items-center gap-3">
          <p className="line-clamp-1 text-sm font-medium">
            {submission.activity.title}
          </p>
          {statusBadge(submission)}
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4 shrink-0" />
            {submission.submitted_at
              ? new Date(submission.submitted_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })
              : "N/A"}
          </div>
          {submission.score !== null &&
            submission.score !== undefined &&
            submission.score !== 0 && (
              <div className="flex items-center gap-1 text-primary">
                <Award className="h-4 w-4" />
                Score: {submission.score}
              </div>
            )}
        </div>
        {submission.admin_comments ? (
          <div className="mt-2 rounded-md border border-border/60 bg-muted/40 p-2 text-sm">
            <p className="mb-1 text-xs font-medium text-muted-foreground">
              Admin Comments:
            </p>
            <p>{submission.admin_comments}</p>
          </div>
        ) : null}
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() =>
          navigate(getLink(`/school/submissions/${submission.id}`))
        }
        className="mt-2 shrink-0 gap-2 sm:mt-0"
      >
        <Eye className="h-4 w-4" />
        View
      </Button>
    </div>
  );

  return (
    <div className="mt-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ListCardViewToggle viewMode={viewMode} onViewChange={setViewMode} />
      </div>
      <div
        className={
          viewMode === "card"
            ? "grid grid-cols-1 items-start gap-4 sm:grid-cols-2 lg:grid-cols-3"
            : "divide-y divide-border"
        }
      >
        {viewMode === "card"
          ? paginatedItems.map(renderGridCard)
          : paginatedItems.map(renderListRow)}
      </div>
      <KarunaPagination
        page={page}
        totalPages={totalPages}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}

const SchoolSubmissions = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const getLink = useKarunaLink();
  const queryClient = useQueryClient();

  // Normalize mobile number helper
  const normalizeMobileNumber = (mobileNumber: string): string => {
    return String(mobileNumber ?? "")
      .replace(/\D/g, "")
      .slice(0, 10);
  };

  // Get user info for filtering submissions
  const userInfo = getUserInfo();
  const normalizedContact = userInfo?.phone_number
    ? normalizeMobileNumber(userInfo.phone_number)
    : "";

  // Fetch school_id from Supabase
  const { data: schoolData } = useQuery({
    queryKey: ["school-id", user?.id],
    queryFn: async () => {
      if (!user?.id) {
        return null;
      }
      try {
        const { data, error } = await supabase
          .from("schools")
          .select("id")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error) {
          throw error;
        }
        return data;
      } catch (error) {
        console.error("Error fetching school_id:", error);
        return null;
      }
    },
    enabled: !!user?.id,
    retry: false,
  });

  const schoolId = schoolData?.id ?? null;

  // Fetch submissions from Activity Submission schema
  const {
    data: submissionsData,
    isLoading: submissionsLoading,
    refetch: refetchSubmissions,
  } = useQuery({
    queryKey: [
      "school-submissions",
      SUBMISSION_SCHEMA_NAME,
      normalizedContact,
      schoolId,
    ],
    queryFn: async () => {
      if (!normalizedContact && !schoolId) {
        console.log("⚠️ No contact or schoolId available for filtering");
        return [];
      }
      try {
        const allSubmissions =
          await DynamicModuleService.getGridColItemsAllPages(
            SUBMISSION_SCHEMA_NAME
          );

        console.log("📋 All submissions fetched:", allSubmissions.length);
        console.log(
          "🔍 Filtering with - schoolId:",
          schoolId,
          "contact:",
          normalizedContact
        );

        // Log the first submission's full structure to understand the data format
        if (allSubmissions.length > 0) {
          console.log(
            "🔬 FULL FIRST SUBMISSION STRUCTURE (from grid):",
            JSON.stringify(allSubmissions[0], null, 2)
          );
          console.log(
            "🔬 First submission keys:",
            Object.keys(allSubmissions[0])
          );

          // Try to fetch full data for the first submission to see all fields
          try {
            const firstId = allSubmissions[0].id ?? allSubmissions[0]._id;
            if (firstId) {
              const fullData = await DynamicModuleService.getItemById(
                SUBMISSION_SCHEMA_NAME,
                firstId
              );
              console.log(
                "🔬 FULL FIRST SUBMISSION (from getItemById):",
                JSON.stringify(fullData, null, 2)
              );
              if (fullData) {
                const data = fullData.data ?? fullData;
                console.log("🔬 Full data keys:", Object.keys(data));
              }
            }
          } catch (err) {
            console.warn("⚠️ Could not fetch full submission data:", err);
          }
        }

        const submissionsWithFullData = await mapWithConcurrency(
          allSubmissions,
          DEFAULT_API_FETCH_CONCURRENCY,
          async (submission: any) => {
            const submissionId = submission.id ?? submission._id;
            if (!submissionId) {
              return submission;
            }

            try {
              const fullData = await DynamicModuleService.getItemById(
                SUBMISSION_SCHEMA_NAME,
                submissionId
              );
              const data = fullData?.data ?? fullData ?? submission;
              // Merge full data with grid data
              return { ...submission, ...data };
            } catch (err) {
              console.warn(
                `⚠️ Could not fetch full data for submission ${submissionId}:`,
                err
              );
              return submission;
            }
          }
        );

        console.log("📦 Fetched full data for all submissions");
        if (submissionsWithFullData.length > 0) {
          console.log(
            "🔬 FIRST SUBMISSION WITH FULL DATA:",
            JSON.stringify(submissionsWithFullData[0], null, 2)
          );
          console.log(
            "🔬 Full data keys:",
            Object.keys(submissionsWithFullData[0])
          );
        }

        // Filter submissions for this school by contact number
        // Since schema doesn't have school_id, we use schoolcontact field
        // Use submissionsWithFullData which has all fields
        const filtered = submissionsWithFullData.filter((submission: any) => {
          // Get contact from various possible field names and structures
          // The data might be stored as:
          // 1. Flat: submission.schoolcontact
          // 2. Nested object: submission.basicInfo?.schoolcontact
          // 3. String path: submission["basicInfo.schoolcontact"]
          // 4. Other variations: contact, school_contact
          // 5. All field names (case-insensitive search)

          let submissionContact = "";

          // Try nested object first (most likely structure)
          if (
            submission.basicInfo &&
            typeof submission.basicInfo === "object"
          ) {
            submissionContact = normalizeMobileNumber(
              submission.basicInfo.schoolcontact ??
                submission.basicInfo.contact ??
                submission.basicInfo.school_contact ??
                ""
            );
          }

          // If not found, try flat fields (check all possible variations)
          if (!submissionContact) {
            // Try exact field names
            submissionContact = normalizeMobileNumber(
              submission.schoolcontact ??
                submission["basicInfo.schoolcontact"] ??
                submission.contact ??
                submission.school_contact ??
                submission.schoolContact ?? // camelCase
                submission.school_Contact ?? // mixed case
                ""
            );
          }

          // If still not found, search all keys (case-insensitive)
          if (!submissionContact) {
            const allKeys = Object.keys(submission);
            const contactKey = allKeys.find(
              key =>
                key.toLowerCase().includes("contact") ||
                key.toLowerCase().includes("schoolcontact")
            );
            if (contactKey) {
              submissionContact = normalizeMobileNumber(
                String(submission[contactKey] ?? "")
              );
            }
          }

          // Debug: log submission data (only for first few to avoid spam)
          if (submissionsWithFullData.indexOf(submission) < 3) {
            console.log("🔎 Checking submission:", {
              id: submission.id ?? submission._id,
              schoolcontact: submission.schoolcontact,
              basicInfo: submission.basicInfo,
              "basicInfo.schoolcontact": submission["basicInfo.schoolcontact"],
              "basicInfo?.schoolcontact": submission.basicInfo?.schoolcontact,
              contact: submission.contact,
              school_contact: submission.school_contact,
              normalized: submissionContact,
              event_id: submission.event_id ?? submission.eventid,
              allKeys: Object.keys(submission).filter(
                k =>
                  k.toLowerCase().includes("contact") ||
                  k.toLowerCase().includes("school")
              ),
            });
          }

          // Match by contact number (this is the primary method since schema has no school_id)
          if (normalizedContact && submissionContact) {
            const matches = submissionContact === normalizedContact;
            if (matches && submissionsWithFullData.indexOf(submission) < 3) {
              console.log("✅ Matched by contact number:", submissionContact);
            }
            return matches;
          }

          return false;
        });

        console.log("✅ Filtered submissions count:", filtered.length);
        return filtered;
      } catch (error) {
        console.error("❌ Error fetching submissions:", error);
        toast({
          title: "Error",
          description: "Failed to load submissions. Please try again.",
          variant: "destructive",
        });
        return [];
      }
    },
    enabled: !!normalizedContact || !!schoolId,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  // Fetch activities to map event_id to activity title
  const { data: activitiesData } = useQuery({
    queryKey: ["activities", ACTIVITY_SCHEMA_NAME],
    queryFn: async () => {
      try {
        return await DynamicModuleService.getGridColItemsAllPages(
          ACTIVITY_SCHEMA_NAME
        );
      } catch (error) {
        console.error("Error fetching activities:", error);
        return [];
      }
    },
  });

  // Create a map of activity ID to title (for fallback if event_id exists)
  const activityMap = useMemo(() => {
    if (!activitiesData || !Array.isArray(activitiesData)) {
      return new Map<string, string>();
    }
    const map = new Map<string, string>();
    activitiesData.forEach((activity: any) => {
      const activityId = String(activity.id ?? activity._id ?? "").trim();
      const title =
        activity.title ??
        activity.activitytitle ??
        activity.activity_title ??
        "Unknown Activity";
      if (activityId) {
        map.set(activityId, title);
      }
    });
    return map;
  }, [activitiesData]);

  // Transform submissions data with program names and dates
  const submissions = useMemo((): Submission[] => {
    if (!submissionsData || !Array.isArray(submissionsData)) {
      return [];
    }

    // Program name mapping (from schema enum values)
    const programNameMap: Record<string, string> = {
      intraschool: "Organising Intra School Programmes",
      interschool: "Organising Inter School Programmes",
      participationinter:
        "Participation in Inter-School Competitions/Programmes",
      publicrelation: "Public Relation Programmes",
      karunaclub: "Karuna Club – Internal / External Growth",
      nationallevel: "Organising / Participation in National Level Programme",
    };

    return submissionsData
      .map((submission: any): Submission | null => {
        const eventId = String(
          submission.event_id ?? submission.eventid ?? ""
        ).trim();
        const submissionId = submission.id ?? submission._id ?? "";

        if (!submissionId) {
          return null;
        }

        // Get program name from submission
        const programValue = submission.program ?? "";
        const programName = programValue
          ? (programNameMap[programValue] ?? programValue)
          : "";

        // Try to get activity title from event_id, otherwise use program name
        let displayTitle = "Unknown Activity";
        if (eventId) {
          const activityTitle = activityMap.get(eventId);
          if (activityTitle) {
            displayTitle = activityTitle;
          } else if (programName) {
            displayTitle = programName;
          }
        } else if (programName) {
          displayTitle = programName;
        }

        // Get submission date - prefer created_on (when it was submitted)
        const submittedAt =
          submission.submitted_at ??
          submission.created_on ??
          submission.created_at ??
          submission.submit_date ??
          submission.updated_on ??
          "";

        return {
          id: submissionId,
          event_id: eventId,
          status: submission.status ?? "pending",
          score: submission.score ?? null,
          submitted_at: submittedAt,
          admin_comments:
            submission.admin_comments ?? submission.admincomments ?? null,
          activity: {
            title: displayTitle,
          },
        };
      })
      .filter((s): s is Submission => s !== null)
      .sort((a, b) => {
        // Sort by submitted_at descending (most recent first)
        const dateA = a.submitted_at ? new Date(a.submitted_at).getTime() : 0;
        const dateB = b.submitted_at ? new Date(b.submitted_at).getTime() : 0;
        return dateB - dateA;
      });
  }, [submissionsData, activityMap]);

  // Listen for submission-created event to refresh
  useEffect(() => {
    const handleSubmissionCreated = () => {
      console.log(
        "🔄 Submission created event received, invalidating and refetching..."
      );
      // Invalidate the query cache and refetch
      queryClient.invalidateQueries({
        queryKey: ["school-submissions", SUBMISSION_SCHEMA_NAME],
      });
      // Also refetch directly
      setTimeout(() => {
        refetchSubmissions();
      }, 1000);
    };
    window.addEventListener("submission-created", handleSubmissionCreated);
    return () => {
      window.removeEventListener("submission-created", handleSubmissionCreated);
    };
  }, [refetchSubmissions, queryClient]);

  const loading = submissionsLoading;

  const stats = [
    {
      label: "Total Submissions",
      value: submissions.length,
      icon: FileText,
      color: "text-blue-600 bg-blue-500/10",
    },
    {
      label: "Approved",
      value: submissions.filter(s => s.status === "approved").length,
      icon: Award,
      color: "text-green-600 bg-green-500/10",
    },
    {
      label: "Pending Review",
      value: submissions.filter(s => s.status === "pending").length,
      icon: TrendingUp,
      color: "text-orange-600 bg-orange-500/10",
    },
    {
      label: "Rejected",
      value: submissions.filter(s => s.status === "rejected").length,
      icon: X,
      color: "text-red-600 bg-red-500/10",
    },
  ];

  return (
    <SchoolLayout>
      <div className="space-y-6">
        <div>
          <h1 className="bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-3xl font-bold">
            My Submissions
          </h1>
          <p className="mt-1 text-muted-foreground">
            Track your submitted activity reports and their status
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {stats.map(stat => (
            <Card key={stat.label} className="border-border/50 shadow-medium">
              <CardContent className="p-6">
                <div
                  className={`h-12 w-12 rounded-lg ${stat.color} mb-4 flex items-center justify-center`}
                >
                  <stat.icon
                    className={`h-6 w-6 ${stat.color.split(" ")[0]}`}
                  />
                </div>
                <div className="mb-1 text-3xl font-bold">{stat.value}</div>
                <div className="text-sm text-muted-foreground">
                  {stat.label}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="border-border/50 shadow-medium">
          <CardHeader>
            <CardTitle>Submission History</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-muted-foreground">
                Loading submissions...
              </div>
            ) : submissions.length === 0 ? (
              <div className="py-12 text-center">
                <FileText className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                <p className="mb-4 text-muted-foreground">No submissions yet</p>
                <Button
                  onClick={() => navigate(getLink("/school/activities"))}
                  className="gap-2"
                >
                  Browse Activities
                </Button>
              </div>
            ) : (
              <Tabs defaultValue="all" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="all">
                    All ({submissions.length})
                  </TabsTrigger>
                  <TabsTrigger value="approved">
                    Approved (
                    {submissions.filter(s => s.status === "approved").length})
                  </TabsTrigger>
                  <TabsTrigger value="pending">
                    Pending (
                    {submissions.filter(s => s.status === "pending").length})
                  </TabsTrigger>
                  <TabsTrigger value="rejected">
                    Rejected (
                    {submissions.filter(s => s.status === "rejected").length})
                  </TabsTrigger>
                </TabsList>

                {(
                  [
                    "all",
                    "approved",
                    "pending",
                    "rejected",
                  ] as SubmissionStatusTab[]
                ).map(tab => (
                  <TabsContent key={tab} value={tab}>
                    <SchoolSubmissionTabPanel
                      tab={tab}
                      submissions={submissions}
                      navigate={navigate}
                      getLink={getLink}
                    />
                  </TabsContent>
                ))}
              </Tabs>
            )}
          </CardContent>
        </Card>
      </div>
    </SchoolLayout>
  );
};

export default SchoolSubmissions;
